import _ from "lodash";
import keyBy from "lodash/keyBy";
import * as React from "react";
import { connect } from "react-redux";

import { Alert, Button, Checkbox, Group, Modal, Stack, Text } from "@mantine/core";
import { getAvailabilities } from "../../../data/actions/availabilityActions";
import { addShiftSessionEnrolment } from "../../../data/actions/shiftActions";
import { IState } from "../../../data/reducers/state";
import {
  IAppointment,
  IAppointmentDetail,
} from "../../../types/appointments/Appointment";
import { AppointmentStatus } from "../../../types/appointments/AppointmentStatus";
import { IAssociation } from "../../../types/association/Association";
import { IDateTimePeriod } from "../../../types/availabilities/Availabilities";
import { BasketballGrade } from "../../../types/common/basketballGrades";
import { IGame } from "../../../types/game/Game";
import { Role } from "../../../types/persons/Role";
import { IPerson } from "../../../types/persons/person";
import { IShiftSession } from "../../../types/shifts/Shift";
import {
  IShiftEnrolment,
  IShiftSessionEnrolment,
} from "../../../types/shifts/ShiftEnrolment";
import { ShiftEnrolmentType } from "../../../types/shifts/enums/ShiftEnrolmentType";
import { toDateTime } from "../../../util/date+helper";
import TextSeparator from "../../common/TextSeparator";
import { CenteredLoader } from "../../common/loader";
import PersonSearcher from "../../common/person/PersonSearcher";
import RosterPersonMoreInfo from "./RosterPersonMoreInfo";

interface ISessionPersonModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPerson: IPerson | null;
  selectedRoles: string[];
  onPersonSelect: (person: IPerson) => void;
  onRolesChange: (roles: string[]) => void;
  onAddPerson: () => Promise<void>;
  isSaving: boolean;
  error: string | null;
  availableRoles: { value: string; label: string }[];
  associationId?: string;
  personsToIgnore: Set<string>;
}

const SessionPersonModal: React.FC<ISessionPersonModalProps> = ({
  isOpen,
  onClose,
  selectedPerson,
  selectedRoles,
  onPersonSelect,
  onRolesChange,
  onAddPerson,
  isSaving,
  error,
  availableRoles,
  associationId,
  personsToIgnore,
}) => (
  <Modal
    opened={isOpen}
    onClose={onClose}
    title="Add person for this session"
    size="lg"
  >
    <Stack gap="md">
      <div>
        <Alert color="yellow">
          Note: Once added, this person cannot currently be removed from the shift session list.
        </Alert>
        <Text size="sm" fw={500}>
          Select person
        </Text>
        <PersonSearcher
          associationId={associationId}
          personsToIgnore={personsToIgnore}
          onChange={onPersonSelect}
        />
        {selectedPerson && (
          <Text size="sm" mt="xs" color="dimmed">
            {selectedPerson.fullName}
          </Text>
        )}
      </div>
      <div>
        <Text size="sm" fw={500}>
          Roles for this session
        </Text>
        <Checkbox.Group
          value={selectedRoles}
          onChange={onRolesChange}
        >
          <Group gap="md" mt="xs">
            {availableRoles.map((option) => (
              <Checkbox key={option.value} value={option.value} label={option.label} />
            ))}
            {availableRoles.length === 0 && selectedPerson && (
              <Text size="sm" color="dimmed">
                This person does not currently hold any rosterable roles for
                this association.
              </Text>
            )}
          </Group>
        </Checkbox.Group>
      </div>
      <Text size="sm" color="dimmed">
        This adds the official only to this shift session. They will not be
        added to the shift permanently.
      </Text>
      {error && (
        <Text size="sm" color="red">
          {error}
        </Text>
      )}
      <Group justify="flex-end">
        <Button
          variant="default"
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          color="blue"
          onClick={onAddPerson}
          loading={isSaving}
          disabled={!selectedPerson || selectedRoles.length === 0}
        >
          Add to session
        </Button>
      </Group>
    </Stack>
  </Modal>
);

interface IRefereePayGridProps {
  referees?: IShiftSessionEnrolment[];
  gameIds?: string[];

  shiftSessionId?: string;
  shiftSession?: IShiftSession;
  gridView?: string | null;

  persons?: { [personId: string]: IPerson };
  games?: { [gameId: string]: IGame };
  appointmentsMap?: {
    [appointmentId: string]: IAppointment<IAppointmentDetail>;
  };
  appointmentsByPerson?: { [personId: string]: string[] };
  enrolments?: { [key: string]: IShiftEnrolment };
  association?: IAssociation;
  addSessionEnrolment: (enrolment: IShiftEnrolment) => Promise<IShiftEnrolment>;
  refreshSession: () => Promise<void>;
  getAvailabilities: (personId: string, options?: any) => Promise<any>;
}

enum OverallAppointmentActionStatus {
  unappointed = "unappointed",
  waiting = "waiting",
  confirmed = "confirmed",
  completed = "completed",
  requiresAction = "actionRequired",
  requiresRepublish = "republishRequired",
  permanent = "permanent",
  casual = "casual",
  unavailable = "unavailable",
  emergency = "emergency",
}

const AppointmentActionStatusOrder = [
  OverallAppointmentActionStatus.requiresAction,
  OverallAppointmentActionStatus.requiresRepublish,
  OverallAppointmentActionStatus.waiting,
  OverallAppointmentActionStatus.completed,
  OverallAppointmentActionStatus.confirmed,
  OverallAppointmentActionStatus.unappointed,
  OverallAppointmentActionStatus.permanent,
  OverallAppointmentActionStatus.casual,
  OverallAppointmentActionStatus.emergency,
  OverallAppointmentActionStatus.unavailable,
];

const SESSION_ROLE_OPTIONS = [
  { value: Role.referee.toString(), label: "Referee" },
  { value: Role.scoretable.toString(), label: "Scoretable" },
  { value: Role.statistician.toString(), label: "Statistician" },
  { value: Role.refereeCoach.toString(), label: "Referee Coach" },
  { value: Role.supervisor.toString(), label: "Supervisor" },
  { value: Role.administrative.toString(), label: "Administrative" },
];

namespace OverallAppointmentActionStatus {
  export function toString(status: OverallAppointmentActionStatus) {
    switch (status) {
      case OverallAppointmentActionStatus.unappointed:
        return "Unappointed";
      case OverallAppointmentActionStatus.waiting:
        return "Rostered";
      case OverallAppointmentActionStatus.confirmed:
        return "Confirmed";
      case OverallAppointmentActionStatus.completed:
        return "Completed";
      case OverallAppointmentActionStatus.requiresAction:
        return "Action required";
      case OverallAppointmentActionStatus.requiresRepublish:
        return "Republish required";
      case OverallAppointmentActionStatus.permanent:
        return "Permanent";
      case OverallAppointmentActionStatus.casual:
        return "Casual";
      case OverallAppointmentActionStatus.unavailable:
        return "Unavailable";
      case OverallAppointmentActionStatus.emergency:
        return "Emergency";
    }
    return "";
  }

  export function getBackgroundColor(status: OverallAppointmentActionStatus) {
    switch (status) {
      case OverallAppointmentActionStatus.unappointed:
        return "";
      case OverallAppointmentActionStatus.completed:
        return "var(--color-success-bg)";
      case OverallAppointmentActionStatus.waiting:
        return "var(--color-warning-bg)";
      case OverallAppointmentActionStatus.confirmed:
        return "var(--color-success-bg)";
      case OverallAppointmentActionStatus.requiresAction:
        return "var(--color-danger-bg)";
      case OverallAppointmentActionStatus.requiresRepublish:
        return "var(--color-info-bg)";
      case OverallAppointmentActionStatus.permanent:
        return "var(--color-success-bg)";
      case OverallAppointmentActionStatus.casual:
        return "var(--color-info-bg)";
      case OverallAppointmentActionStatus.unavailable:
        return "var(--color-warning-bg)";
      case OverallAppointmentActionStatus.emergency:
        return "var(--color-info-bg)";
    }
    return "";
  }

  export function getTextColor(status: OverallAppointmentActionStatus) {
    switch (status) {
      case OverallAppointmentActionStatus.unappointed:
        return "";
      case OverallAppointmentActionStatus.completed:
        return "var(--color-success-text)";
      case OverallAppointmentActionStatus.waiting:
        return "var(--color-warning-text)";
      case OverallAppointmentActionStatus.confirmed:
        return "var(--color-success-text)";
      case OverallAppointmentActionStatus.requiresAction:
        return "var(--color-danger-text)";
      case OverallAppointmentActionStatus.requiresRepublish:
        return "var(--color-info-text)";
      case OverallAppointmentActionStatus.permanent:
        return "var(--color-success-text)";
      case OverallAppointmentActionStatus.casual:
        return "var(--color-info-text)";
      case OverallAppointmentActionStatus.unavailable:
        return "var(--color-warning-text)";
      case OverallAppointmentActionStatus.emergency:
        return "var(--color-info-text)";
    }
    return "";
  }

  export function fromAppointments(
    shiftRange: IDateTimePeriod,
    appointments: IAppointment<any>[],
    enrolment: IShiftSessionEnrolment,
  ): { status: OverallAppointmentActionStatus; subtitle?: string } {

    const status = enrolment.type;
    const availTimes = enrolment.availability
      .map(
        (r) =>
          `${toDateTime(r.start).toFormat("hh:mm a")} to ${toDateTime(
            r.finish,
          ).toFormat("hh:mm a")}`,
      )
      .join(", ");

    if (enrolment.availability.length === 0 && appointments.length === 0) {
      return { status: OverallAppointmentActionStatus.unavailable };
    }

    if (!appointments || appointments.length === 0) {
      let ostatus: OverallAppointmentActionStatus =
        OverallAppointmentActionStatus.emergency;

      switch (status) {
        case ShiftEnrolmentType.permanent: {
          ostatus = OverallAppointmentActionStatus.permanent;
          break;
        }
        case ShiftEnrolmentType.casual: {
          ostatus = OverallAppointmentActionStatus.casual;
          break;
        }
        case ShiftEnrolmentType.emergency: {
          ostatus = OverallAppointmentActionStatus.emergency;
          break;
        }
      }

      return { status: ostatus, subtitle: availTimes };

      // if (status == ShiftEnrolmentType.permanent || status == ShiftEnrolmentType.casual) {
      //     const ostatus = status == ShiftEnrolmentType.permanent ? OverallAppointmentActionStatus.permanent : OverallAppointmentActionStatus.casual
      //     return { status: ostatus, subtitle: availTimes }
      //     // if (enrolment.availability.length > 1) {
      //     //     // will only ever be partial
      //     // }

      //     // const availabilityStatus = isCovered(shiftRange, relevant[0])
      //     // if (availabilityStatus == AvailabilityCoverageStatus.fullyCovered) {
      //     //     // unavailable
      //     //     return { status: OverallAppointmentActionStatus.unavailable }
      //     // } else if (availabilityStatus == AvailabilityCoverageStatus.partiallyCovered) {
      //     //     const ostatus = status == ShiftEnrolmentType.permanent ? OverallAppointmentActionStatus.permanent : OverallAppointmentActionStatus.casual
      //     //     return { status: ostatus, subtitle: availTimes }
      //     // } else {
      //     //     const ostatus = status == ShiftEnrolmentType.permanent ? OverallAppointmentActionStatus.permanent : OverallAppointmentActionStatus.casual
      //     //     return { status: ostatus, subtitle: "" }
      //     // }
      // } else {
      //     // emergency
      //     const relevant = condenseRanges((availabilities || []).filter(a => a.available)).filter(a => hasOverlap(shiftRange, a))
      //     const availabilityStatus = isCovered(shiftRange, relevant[0])
      //     if (availabilityStatus == AvailabilityCoverageStatus.fullyCovered) {
      //         // available
      //         return { status: OverallAppointmentActionStatus.emergency }
      //     } else if (availabilityStatus == AvailabilityCoverageStatus.partiallyCovered) {
      //         const availTimes = relevant.map(r => `${r.start.toFormat("hh:mm a")} to ${r.finish.toFormat("hh:mm a")}`).join(", ")
      //         return { status: OverallAppointmentActionStatus.emergency, subtitle: availTimes }
      //     } else {
      //         return { status: OverallAppointmentActionStatus.unavailable }
      //     }
      // }
    }


    if (
      appointments.some((a) => !a || AppointmentStatus.requiresAction(a.status))
    )
      return { status: OverallAppointmentActionStatus.requiresAction };
    if (appointments.some((a) => a.status == AppointmentStatus.rostered))
      return { status: OverallAppointmentActionStatus.requiresRepublish };
    if (appointments.some((a) => a.status == AppointmentStatus.published))
      return { status: OverallAppointmentActionStatus.waiting };
    if (
      appointments.every(
        (a) =>
          a.status == AppointmentStatus.completed ||
          a.status == AppointmentStatus.paymentProcessed ||
          a.status == AppointmentStatus.paymentProcessedInPerson,
      )
    )
      return { status: OverallAppointmentActionStatus.completed };
    return { status: OverallAppointmentActionStatus.confirmed };
  }
}

const personListSorter = (
  persons: string[],
  personMap: { [key: string]: IPerson },
) =>
  persons.sort((a, b) => {
    // 1 for 'a' towards end
    // -1 for 'a' towards start

    // const ar = personMap[a]
    // const br = personMap[b]
    // if (!ar || !ar.referee || !ar.referee.grade) return 1;
    // if (!br || !br.referee || !br.referee.grade) return -1;
    // const aGrade = ar.referee.grade;
    // const bGrade = br.referee.grade;
    // if (aGrade < bGrade) return 1;   // push a towards end
    // if (bGrade < aGrade) return -1;  // push a towards start
    // compare date?
    // const aLName = (ar.preferredName || ar.firstName) + ar.lastName;
    // const bLName = (br.preferredName || br.firstName) + br.lastName;
    const aLName = personMap[a]?.fullName;
    const bLName = personMap[b]?.fullName;
    if (!aLName) return 1;
    if (!bLName) return -1;
    return aLName.localeCompare(bLName);
  });

interface IPersonStatusSectionProps {
  status: OverallAppointmentActionStatus;
  persons: string[];
  personToStatus: { [person: string]: { person: string; status: OverallAppointmentActionStatus; subtitle?: string } };
  relevantAppointmentsByPerson: { [person: string]: IAppointment<any>[] };
  personsMap: { [personId: string]: IPerson };
  enrolments: { [key: string]: IShiftEnrolment };
  setPersonMoreInfo: (personId: string) => void;
}

const PersonStatusSection: React.FC<IPersonStatusSectionProps> = ({
  status,
  persons,
  personToStatus,
  relevantAppointmentsByPerson,
  personsMap,
  enrolments,
  setPersonMoreInfo,
}) => (
  <div key={`ordered-status-section-${status}`}>
    <TextSeparator>
      {OverallAppointmentActionStatus.toString(status)}
    </TextSeparator>
    <table style={{ width: "100%" }}>
      <tbody>
        {personListSorter(persons, personsMap).map((person) => (
          <PersonRow
            key={`official-row-in-pay-grid-${person}`}
            status={status}
            appointments={relevantAppointmentsByPerson[person]}
            person={personsMap[person]}
            subtitle={personToStatus[person].subtitle}
            enrolment={enrolments[person]}
            setPersonMoreInfo={setPersonMoreInfo}
          />
        ))}
      </tbody>
    </table>
  </div>
);

interface IPersonRowProps {
  status: OverallAppointmentActionStatus;
  appointments: IAppointment<IAppointmentDetail>[];
  person: IPerson;
  subtitle?: string | null;
  enrolment: IShiftEnrolment;
  setPersonMoreInfo: (personId: string) => void;
}

const PersonRow: React.FC<IPersonRowProps> = ({
  status,
  appointments,
  person,
  subtitle,
  enrolment,
  setPersonMoreInfo,
}) => {
  const hasAppointments = appointments?.length > 0;
  const colspan = hasAppointments ? 0 : 1;
  const grade = person?.referee?.grade ? (
    <> ({BasketballGrade.componentFromPersonWithDecorations(person)})</>
  ) : (
    ""
  );

  return (
    <React.Fragment key={`official-row-in-pay-grid-${person._id}`}>
      <tr style={{ padding: "2px" }}>
        <td colSpan={colspan}>
          <button
            className="btn btn-link"
            onClick={() => setPersonMoreInfo(person?._id)}
            style={{
              textAlign: "left",
              padding: "0px",
              color: "var(--color-text-primary)",
              textDecoration: "none",
            }}
          >
            {person.fullName}
            {grade}
            {hasAppointments && (
              <span
                style={{
                  padding: "4px 6px",
                  marginLeft: "5px",
                  borderRadius: "15px",
                  fontSize: "14px",
                  background:
                    OverallAppointmentActionStatus.getBackgroundColor(status),
                  color: OverallAppointmentActionStatus.getTextColor(status),
                }}
                className="noselect"
              >
                ({appointments.length})
              </span>
            )}
          </button>
        </td>
      </tr>
      {subtitle && (
        <tr>
          <td colSpan={colspan}>
            <em style={{ color: "darkgray" }}>{subtitle}</em>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

const RosterPersons: React.FunctionComponent<IRefereePayGridProps> = (
  props,
) => {
  const [modalState, setModalState] = React.useState({
    isOpen: false,
    selectedPerson: null as IPerson | null,
    selectedRoles: [Role.referee.toString()],
    isSaving: false,
    error: null as string | null,
  });

  const [personIdMoreInfo, setPersonMoreInfo] = React.useState<string | null>(
    null,
  );

  const {
    referees = [],
    persons = {},
    appointmentsMap = {},
    appointmentsByPerson = {},
    enrolments = {},
  } = props;

  const associationId = props.association?._id?.toString();

  const availableSessionRoleOptions = React.useMemo(() => {
    if (!modalState.selectedPerson || !associationId) return [];
    const associationRecord = modalState.selectedPerson.associations?.find(
      (assoc) => assoc.associationId?.toString() === associationId,
    );
    const roleMask = associationRecord?.role as Role | undefined;
    if (!roleMask) return [];
    return SESSION_ROLE_OPTIONS.filter((option) =>
      Role.hasRole(roleMask, Number(option.value) as Role),
    );
  }, [associationId, modalState.selectedPerson]);

  React.useEffect(() => {
    if (!modalState.selectedPerson) return;
    const defaults = availableSessionRoleOptions.map((option) => option.value);
    setModalState(prev => ({ ...prev, selectedRoles: defaults }));
  }, [availableSessionRoleOptions, modalState.selectedPerson]);

  const personsToIgnore = React.useMemo(
    () => new Set((referees || []).map((ref) => ref.person)),
    [referees],
  );

  const { addSessionEnrolment, refreshSession } = props;
  const shiftContextId = props.shiftSession?.shift?.toString() || '';
  const sessionContextId = props.shiftSessionId;

  const openAddPersonModal = React.useCallback(() => {
    setModalState({
      isOpen: true,
      selectedPerson: null,
      selectedRoles: [Role.referee.toString()],
      isSaving: false,
      error: null,
    });
  }, []);

  const closeAddPersonModal = React.useCallback(() => {
    if (modalState.isSaving) return;
    setModalState({
      isOpen: false,
      selectedPerson: null,
      selectedRoles: [Role.referee.toString()],
      isSaving: false,
      error: null,
    });
  }, [modalState.isSaving]);

  const handleRolesChange = React.useCallback((roles: string[]) => {
    setModalState(prev => ({ ...prev, selectedRoles: roles }));
  }, []);

  const handleSessionPersonSelected = React.useCallback((person: IPerson) => {
    setModalState(prev => ({ ...prev, selectedPerson: person, error: null }));
  }, []);

  const addPersonToSession = React.useCallback(async () => {
    if (!modalState.selectedPerson) {
      setModalState(prev => ({ ...prev, error: "Select a person to add" }));
      return;
    }
    if (modalState.selectedRoles.length === 0) {
      setModalState(prev => ({ ...prev, error: "Select at least one role" }));
      return;
    }

    const roleValue = modalState.selectedRoles.reduce<number>(
      (prev, value) => prev | Number(value),
      0,
    ) as Role;

    const payload: IShiftEnrolment = {
      person: modalState.selectedPerson._id,
      shift: shiftContextId,
      shiftSession: sessionContextId,
      role: roleValue,
      type: ShiftEnrolmentType.casual,
    };

    setModalState(prev => ({ ...prev, isSaving: true }));
    try {
      await addSessionEnrolment(payload);
      await refreshSession();
      setModalState({
        isOpen: false,
        selectedPerson: null,
        selectedRoles: [Role.referee.toString()],
        isSaving: false,
        error: null,
      });
    } catch (error: unknown) {
      setModalState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Unable to add person to session'
      }));
    } finally {
      setModalState(prev => ({ ...prev, isSaving: false }));
    }
  }, [
    addSessionEnrolment,
    refreshSession,
    shiftContextId,
    sessionContextId,
    modalState.selectedPerson,
    modalState.selectedRoles,
  ]);

  if (!referees) {
    return (
      <p style={{ color: "grey" }}>
        <em>No referees</em>
      </p>
    );
  }
  const refMap = keyBy(referees, "person");
  const refPersonIds = Object.keys(refMap);

  // we'll group the referees into their status
  const relevantAppointmentsByPerson: {
    [person: string]: IAppointment<any>[];
  } = {}
  refPersonIds.forEach(person => {
    relevantAppointmentsByPerson[person] = (appointmentsByPerson[person] || [])
      .map((apid) => appointmentsMap[apid])
      .filter(Boolean)
      .filter(
        (ap) =>
          ap.details &&
          ap.shiftSession === props.shiftSessionId &&
          ap.stillAppointed,
      );
  })

  if (!props.shiftSession) return <CenteredLoader />;

  const shiftRange = {
    start: toDateTime(props.shiftSession.start),
    finish: toDateTime(props.shiftSession.finish),
  };
  const personToStatus: {
    [person: string]: {
      person: string;
      status: OverallAppointmentActionStatus;
      subtitle?: string;
    };
  } = {}

  refPersonIds.forEach(person => {
    personToStatus[person] = {
      person,
      ...OverallAppointmentActionStatus.fromAppointments(
        shiftRange,
        relevantAppointmentsByPerson[person],
        refMap[person],
      ),
    }

    const enrolmentRecord = enrolments[person];
    if (enrolmentRecord?.shiftSession) {
      const tag = enrolmentRecord.shiftSession === props.shiftSessionId
        ? "Session-only enrolment"
        : "Session enrolment";
      personToStatus[person].subtitle = personToStatus[person].subtitle
        ? `${personToStatus[person].subtitle} • ${tag}`
        : tag;
    }
    if (enrolmentRecord?.autoGenerated) {
      const tag = "Association permanent";
      personToStatus[person].subtitle = personToStatus[person].subtitle
        ? `${personToStatus[person].subtitle} • ${tag}`
        : tag;
    }
  })

  const sessionOnlyUnassignedPeople = refPersonIds.filter((person) => {
    const enrolmentRecord = enrolments[person];
    if (!enrolmentRecord?.shiftSession) return false;
    if (enrolmentRecord.shiftSession !== sessionContextId) return false;
    const appointments = relevantAppointmentsByPerson[person] || [];
    return appointments.length === 0;
  });

  const sessionOnlyUnassignedSet = new Set(sessionOnlyUnassignedPeople);

  const byStatus = _.groupBy(
    Object.values(personToStatus).filter(
      (entry) => !sessionOnlyUnassignedSet.has(entry.person),
    ),
    (obj) => obj.status,
  );

  const orderedStatuses = AppointmentActionStatusOrder.filter(
    (st) => st in byStatus,
  );

  const sections = orderedStatuses.map((st) => (
    <PersonStatusSection
      key={`ordered-status-section-${st}`}
      status={st}
      persons={byStatus[st].map((a) => a.person)}
      personToStatus={personToStatus}
      relevantAppointmentsByPerson={relevantAppointmentsByPerson}
      personsMap={persons}
      enrolments={enrolments}
      setPersonMoreInfo={setPersonMoreInfo}
    />
  ));

  return (
    <>
      <Group justify="flex-end" mb="sm">
        <Button size="xs" variant="outline" color="blue" onClick={openAddPersonModal}>
          Add person for this session
        </Button>
      </Group>
      {!!sessionOnlyUnassignedPeople.length && (
        <div>
          <TextSeparator>Session-only (unassigned)</TextSeparator>
          <table style={{ width: "100%" }}>
            <tbody>
              {personListSorter(sessionOnlyUnassignedPeople, persons).map((person) => (
                <React.Fragment key={`session-only-row-${person}`}>
                  <PersonRow
                    status={personToStatus[person].status}
                    appointments={relevantAppointmentsByPerson[person]}
                    person={persons[person]}
                    subtitle={personToStatus[person].subtitle}
                    enrolment={enrolments[person]}
                    setPersonMoreInfo={setPersonMoreInfo}
                  />
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sections}
      <SessionPersonModal
        isOpen={modalState.isOpen}
        onClose={closeAddPersonModal}
        selectedPerson={modalState.selectedPerson}
        selectedRoles={modalState.selectedRoles}
        onPersonSelect={handleSessionPersonSelected}
        onRolesChange={handleRolesChange}
        onAddPerson={addPersonToSession}
        isSaving={modalState.isSaving}
        error={modalState.error}
        availableRoles={availableSessionRoleOptions}
        associationId={props.association?._id}
        personsToIgnore={personsToIgnore}
      />
      {personIdMoreInfo && (
        <Modal
          opened={!!personIdMoreInfo}
          onClose={() => setPersonMoreInfo(null)}
          size="xl"
          title="Person Details"
        >
          <RosterPersonMoreInfo
            person={persons?.[personIdMoreInfo!]}
            association={props.association!}
            shiftContext={props.shiftSession?.shift || ''}
            relevantTime={props.shiftSession ? toDateTime(props.shiftSession.start) : undefined}
            appointments={props.appointmentsByPerson?.[personIdMoreInfo!]?.map(
              (apId) => appointmentsMap?.[apId],
            ).filter((ap): ap is IAppointment<IAppointmentDetail> => Boolean(ap)) || []}
            hasAvailabilities={(props as any).availabilities?.byPerson ? personIdMoreInfo! in (props as any).availabilities?.byPerson : false}
            actions={{
              getAvailabilities: (personId: string) => props.getAvailabilities!(personId, {}),
            }}
          />
          <Group mt="md" justify="flex-end">
            <Button
              color="gray"
              onClick={() => setPersonMoreInfo(null)}
            >
              Close
            </Button>
          </Group>
        </Modal>
      )}
    </>
  );
};

function mapStateToProps(
  state: IState,
  ownProps: IRefereePayGridProps,
): Partial<IRefereePayGridProps> {
  return {
    persons: state.persons.map,
    games: state.games,
    appointmentsMap: state.appointments.map,
    appointmentsByPerson: state.appointments.byPerson,
  };
}

function mapDispatchToProps(dispatch: any): Partial<IRefereePayGridProps> {
  return {
    addSessionEnrolment: (enrolment: IShiftEnrolment) =>
      dispatch(addShiftSessionEnrolment(enrolment)),
    getAvailabilities: (personId: string, options?: any) =>
      dispatch(getAvailabilities(personId, options || {})),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(RosterPersons);
